import { useState } from 'react';

interface FreeResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FreeResourceModal = ({ isOpen, onClose }: FreeResourceModalProps) => {
  const [formData, setFormData] = useState({
    consulting: '',
    name: '',
    company: '',
    manager: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5a6rqefmrgp9cvlutjg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          consulting: '',
          name: '',
          company: '',
          manager: '',
          phone: '',
          email: ''
        });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">업종별 무료 자료 받기</h2>
            <p className="text-sm text-slate-600 mt-1">산업안전 전문가가 분석한 2026년 최신 감독 지적 사례집 및 적발 사례 Top 4 자료를 이메일로 발송해드리고 있습니다. 정확한 상담을 위해 아래 정보를 입력해주세요.</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-slate-600"></i>
          </button>
        </div>

        <form id="free-resource-form" data-readdy-form onSubmit={handleSubmit} className="px-8 py-6">
          <div className="space-y-5">
            <div>
              <label htmlFor="consulting" className="block text-sm font-semibold text-slate-900 mb-2">
                원하는 컨설팅 <span className="text-red-500">*</span>
              </label>
              <select
                id="consulting"
                name="consulting"
                value={formData.consulting}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">선택해주세요</option>
                <option value="위험성평가">위험성평가</option>
                <option value="근골격계유해요인조사">근골격계유해요인조사</option>
                <option value="안전보건체계구축">안전보건체계구축</option>
                <option value="유해위험방지계획서">유해위험방지계획서</option>
                <option value="안전관리 위탁(대행)">안전관리 위탁(대행)</option>
                <option value="KCs인증">KCs인증</option>
              </select>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                이름 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="이름을 입력해주세요"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-semibold text-slate-900 mb-2">
                회사명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                placeholder="회사명을 입력해주세요"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="manager" className="block text-sm font-semibold text-slate-900 mb-2">
                담당자명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="manager"
                name="manager"
                value={formData.manager}
                onChange={handleChange}
                required
                placeholder="담당자명을 입력해주세요"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                전화번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="010-0000-0000"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                이메일 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="example@company.com"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm font-medium flex items-center">
                <i className="ri-checkbox-circle-fill text-lg mr-2"></i>
                신청이 완료되었습니다! 곧 자료를 보내드리겠습니다.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-medium flex items-center">
                <i className="ri-error-warning-fill text-lg mr-2"></i>
                신청 중 오류가 발생했습니다. 다시 시도해주세요.
              </p>
            </div>
          )}

          <div className="mt-8 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? '신청 중...' : '무료 자료 받기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FreeResourceModal;
